import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, del, post } from '../lib/api';
import type { ChatMessage } from '../lib/types';
import { Device, Notice, Spinner } from './Device';

interface ChatPayload { messages: ChatMessage[]; battery: number; depleted: boolean; generating: boolean }

export function ChatFeed({ messages, onWithdraw, canWithdraw }: { messages: ChatMessage[]; onWithdraw?: (message: ChatMessage) => void; canWithdraw?: (message: ChatMessage) => boolean }) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages.length]);
  return (
    <div className="chat-feed" aria-live="polite">
      {messages.map((message) => (
        <article key={message.id} className={`message message--${message.author_type} ${message.source === 'boot' ? 'message--boot' : ''}`}>
          <header><strong>{message.author_name}</strong><time>{new Date(`${message.created_at}Z`).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</time></header>
          <p>{message.content}</p>
          {onWithdraw && canWithdraw?.(message) && <button type="button" className="withdraw" onClick={() => onWithdraw(message)}>RITIRA</button>}
        </article>
      ))}
      <div ref={endRef} />
    </div>
  );
}

export function PlayerChat({ name, playerName, initialBattery, onLogout }: { name: string; playerName: string; initialBattery: number; onLogout: () => Promise<void> }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [battery, setBattery] = useState(initialBattery);
  const [depleted, setDepleted] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const lastId = useRef(0);

  const poll = useCallback(async () => {
    try {
      const data = await api<ChatPayload>(`/chat?after=${lastId.current}`);
      if (data.messages.length) {
        lastId.current = data.messages.at(-1)?.id ?? lastId.current;
        setMessages((current) => {
          const merged = [...current, ...data.messages.filter((next) => !current.some((item) => item.id === next.id))];
          // Una risposta del Gatekeeper implica che i messaggi precedenti sono stati elaborati.
          const lastReply = merged.reduce((max, item) => item.author_type === 'gatekeeper' ? Math.max(max, item.id) : max, 0);
          return lastReply ? merged.map((item) => item.id < lastReply && !item.processed ? { ...item, processed: 1 } : item) : merged;
        });
      }
      setBattery(data.battery);
      setDepleted(data.depleted);
      setGenerating(data.generating);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) { void onLogout(); return; }
      setError(cause instanceof Error ? cause.message : 'Segnale interrotto.');
    }
  }, [onLogout]);

  useEffect(() => {
    void poll();
    const timer = window.setInterval(() => void poll(), 5000);
    return () => window.clearInterval(timer);
  }, [poll]);

  async function send(event: React.FormEvent) {
    event.preventDefault();
    if (!content.trim()) return;
    setSending(true); setError('');
    try {
      const result = await post<{ message: ChatMessage }>('/chat/message', { content });
      setMessages((current) => [...current, result.message]);
      setContent('');
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Invio fallito.'); }
    finally { setSending(false); }
  }

  async function activate() {
    setGenerating(true); setError('');
    try { await post('/chat/activate'); await poll(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Attivazione fallita.'); }
    finally { setGenerating(false); }
  }

  async function withdraw(message: ChatMessage) {
    setError('');
    try {
      await del(`/chat/message/${message.id}`);
      setMessages((current) => current.filter((item) => item.id !== message.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Ritiro fallito.');
      await poll();
    }
  }

  if (depleted) return (
    <Device title={name} battery={0} actions={<button className="ghost" onClick={() => void onLogout()}>DISCONNETTI</button>}>
      <div className="depleted"><div className="depleted__icon">▱</div><h2>BATTERIA COGNITIVA ESAURITA</h2><p>Il nucleo non risponde. L’archivio è entrato in silenzio permanente.</p><code>ERR_PWR_000 // RECOVERY IMPOSSIBILE</code></div>
    </Device>
  );

  return (
    <Device title={name} battery={battery} actions={<div className="identity-bar"><span>OPERATORE: {playerName}</span><button className="text-button" onClick={() => void onLogout()}>ESCI</button></div>}>
      <ChatFeed
        messages={messages}
        onWithdraw={(message) => void withdraw(message)}
        canWithdraw={(message) => !generating && message.author_type === 'player' && !message.processed && message.author_name === playerName}
      />
      {generating && <Spinner label={`${name.toUpperCase()} ELABORA`} />}
      {error && <Notice kind="error">{error}</Notice>}
      <form className="composer" onSubmit={send}>
        <textarea aria-label="Messaggio" placeholder="Scrivi nel canale condiviso..." value={content} onChange={(event) => setContent(event.target.value)} maxLength={2000} rows={2} />
        <button disabled={sending || !content.trim()}>{sending ? '...' : 'INVIA'}</button>
      </form>
      <button className="activate" onClick={() => void activate()} disabled={generating}>
        <span className="activate__lamp" /> {generating ? 'NUCLEO OCCUPATO' : `INTERROGA ${name.toUpperCase()}`}
      </button>
    </Device>
  );
}
